/**
 * Hook to sync local classes to server
 *
 * When the local DB connects, this hook checks for classes that don't have
 * a serverClassId and syncs them to the server.
 */

import { useEffect, useRef, useState } from "react";
import { useLocalDbStore } from "@/store/localDbStore";
import { classService } from "@/services/class.service";
import { setServerClassId, getUnsyncedClasses } from "@/lib/local-db";
import { toast } from "sonner";

function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  return false;
}

export function useClassSync() {
  const db = useLocalDbStore((s) => s.db);
  const isConnected = useLocalDbStore((s) => s.isConnected);
  const markDirty = useLocalDbStore((s) => s.markDirty);

  const [syncing, setSyncing] = useState(false);
  const [syncedCount, setSyncedCount] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    // Only run once when DB connects
    if (!isConnected || !db || hasRun.current) {
      return;
    }

    const controller = new AbortController();

    const syncClasses = async () => {
      // Get classes that need syncing
      const unsyncedClasses = getUnsyncedClasses(db);

      if (unsyncedClasses.length === 0) {
        return;
      }

      hasRun.current = true;
      setSyncing(true);

      let synced = 0;
      const errors: string[] = [];

      for (const localClass of unsyncedClasses) {
        if (controller.signal.aborted) break;

        try {
          // Create on server
          const serverClass = await classService.createClass(
            { name: localClass.name, grade: localClass.grade || undefined },
            controller.signal
          );

          if (controller.signal.aborted) break;

          // Update local class with server ID
          setServerClassId(db, localClass.id, serverClass.classId);
          synced++;
        } catch (err) {
          if (isRequestCancelled(err)) break;
          console.error(`Failed to sync class ${localClass.name}:`, err);
          errors.push(localClass.name);
        }
      }

      if (controller.signal.aborted) return;

      setSyncedCount(synced);
      setSyncing(false);

      if (synced > 0) {
        // Mark DB dirty so it gets saved with new serverClassIds
        markDirty();
        toast.success(`Synced ${synced} class${synced > 1 ? "es" : ""} to cloud`);
      }

      if (errors.length > 0) {
        toast.error(`Failed to sync ${errors.length} class${errors.length > 1 ? "es" : ""}`);
      }
    };

    syncClasses();

    return () => controller.abort();
  }, [isConnected, db, markDirty]);

  // Reset when DB disconnects
  useEffect(() => {
    if (!isConnected) {
      hasRun.current = false;
      setSyncedCount(0);
    }
  }, [isConnected]);

  return { syncing, syncedCount };
}
